import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import {
  TrendingUp, BarChart2, Star, Plus, AlertCircle,
  CheckCircle2, Search, Loader2, RefreshCw,
} from 'lucide-react';
import { AppLayout } from '@/components/AppLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/StatusBadge';
import { useAllEvaluations } from '@/hooks/useEvaluations';
import { useSyncFromNostr } from '@/hooks/useEvaluations';
import { useWatchlist } from '@/hooks/useWatchlist';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import type { EvaluationReport } from '@/types/stock';
import { formatPercent, formatNumber } from '@/lib/analysisEngine';

function EvaluationCard({ report }: { report: EvaluationReport }) {
  const avgMargin = report.annualMetrics.length > 0
    ? report.annualMetrics.reduce((s, m) => s + (m.profitMargin ?? 0), 0) / report.annualMetrics.length
    : null;

  const latestEps = report.annualMetrics[report.annualMetrics.length - 1]?.eps ?? null;

  return (
    <Link to={`/evaluation/${report.id}`}>
      <Card className="hover:border-primary/50 hover:shadow-md transition-all cursor-pointer h-full">
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-2 mb-3">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold font-mono">{report.ticker}</span>
                <StatusBadge status={report.overallStatus} size="sm" />
              </div>
              <p className="text-xs text-muted-foreground truncate max-w-[160px]">{report.companyName}</p>
            </div>
            <div className="text-right">
              <span className={`text-2xl font-bold ${
                report.score >= 70 ? 'text-emerald-600' :
                report.score >= 40 ? 'text-amber-600' :
                'text-red-600'
              }`}>{report.score}</span>
              <span className="text-xs text-muted-foreground">/100</span>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 mb-3">
            <div className="text-center p-1.5 bg-muted/40 rounded">
              <p className="text-xs text-muted-foreground">Net Margin</p>
              <p className="text-sm font-medium">{formatPercent(avgMargin)}</p>
            </div>
            <div className="text-center p-1.5 bg-muted/40 rounded">
              <p className="text-xs text-muted-foreground">Latest EPS</p>
              <p className="text-sm font-medium">${formatNumber(latestEps, 2)}</p>
            </div>
            <div className="text-center p-1.5 bg-muted/40 rounded">
              <p className="text-xs text-muted-foreground">Years</p>
              <p className="text-sm font-medium">{report.annualMetrics.length}</p>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Badge variant="outline" className="text-xs">{report.sectorAnalysis.gicsSector}</Badge>
            <span className="text-xs text-muted-foreground">
              {new Date(report.evaluationDate * 1000).toLocaleDateString()}
            </span>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

export default function DashboardPage() {
  useSeoMeta({
    title: 'Dashboard — Stock Fundamentals',
    description: 'Personal fundamental analysis dashboard. Track and evaluate stocks.',
  });

  const navigate = useNavigate();
  const { data: evaluations, isLoading } = useAllEvaluations();
  const { data: watchlist } = useWatchlist();
  const { user } = useCurrentUser();
  const { mutate: syncFromNostr, isPending: isSyncing } = useSyncFromNostr();
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  const filtered = (evaluations ?? [])
    .filter((e) =>
      (filterStatus === 'all' || e.overallStatus === filterStatus) &&
      (
        e.ticker.toLowerCase().includes(search.toLowerCase()) ||
        e.companyName.toLowerCase().includes(search.toLowerCase())
      ),
    )
    .sort((a, b) => b.evaluationDate - a.evaluationDate);

  const passCount = (evaluations ?? []).filter((e) => e.overallStatus === 'PASS').length;
  const watchCount = (evaluations ?? []).filter((e) => e.overallStatus === 'WATCH').length;
  const failCount = (evaluations ?? []).filter((e) => e.overallStatus === 'FAIL').length;

  return (
    <AppLayout>
      <div className="max-w-screen-xl mx-auto px-4 py-8">
        {/* Hero */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center shadow-lg">
                <TrendingUp className="w-5 h-5 text-primary-foreground" />
              </div>
              Stock Fundamentals
            </h1>
            <p className="text-muted-foreground mt-1">
              Personal investment research — track fundamentals, not noise.
            </p>
          </div>
          <div className="flex gap-2">
            {user && (
              <Button variant="outline" size="sm" onClick={() => syncFromNostr()} disabled={isSyncing}>
                {isSyncing ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-1" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-1" />
                )}
                Sync from Nostr
              </Button>
            )}
            <Button onClick={() => navigate('/analyze')} className="gap-2">
              <Plus className="w-4 h-4" /> New Analysis
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          {[
            { label: 'Total Evaluations', value: evaluations?.length ?? 0, icon: BarChart2, color: 'text-primary' },
            { label: 'Pass', value: passCount, icon: CheckCircle2, color: 'text-emerald-600' },
            { label: 'Watch', value: watchCount, icon: AlertCircle, color: 'text-amber-600' },
            { label: 'Watchlist', value: watchlist?.length ?? 0, icon: Star, color: 'text-blue-500' },
          ].map((stat) => (
            <Card key={stat.label}>
              <CardContent className="py-4 px-4 flex items-center gap-3">
                <stat.icon className={`w-8 h-8 ${stat.color} shrink-0`} />
                <div>
                  <p className="text-2xl font-bold">{stat.value}</p>
                  <p className="text-xs text-muted-foreground">{stat.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filter & Search */}
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search ticker or company…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex gap-2">
            {['all', 'PASS', 'WATCH', 'FAIL'].map((s) => (
              <Button
                key={s}
                variant={filterStatus === s ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterStatus(s)}
              >
                {s === 'all' ? 'All' : s}
              </Button>
            ))}
          </div>
        </div>

        {/* Evaluations Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <Card key={i}>
                <CardContent className="p-4 space-y-3">
                  <Skeleton className="h-6 w-24" />
                  <Skeleton className="h-4 w-40" />
                  <div className="grid grid-cols-3 gap-2">
                    <Skeleton className="h-10" />
                    <Skeleton className="h-10" />
                    <Skeleton className="h-10" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filtered.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.map((report) => (
              <EvaluationCard key={report.id} report={report} />
            ))}
          </div>
        ) : (evaluations?.length ?? 0) === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-16 text-center">
              <BarChart2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <CardTitle className="mb-2">No evaluations yet</CardTitle>
              <CardDescription className="mb-6">
                Run your first fundamental analysis to get started.
              </CardDescription>
              <Button onClick={() => navigate('/analyze')} className="gap-2">
                <Plus className="w-4 h-4" /> Analyze a Stock
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">No results match your filters.</p>
              <Button variant="ghost" className="mt-2" onClick={() => { setSearch(''); setFilterStatus('all'); }}>
                Clear filters
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Recent from Watchlist */}
        {(watchlist?.length ?? 0) > 0 && (
          <div className="mt-10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Star className="w-4 h-4 text-amber-500" /> Watchlist
              </h2>
              <Button variant="ghost" size="sm" onClick={() => navigate('/watchlist')}>
                View all <span className="ml-1 text-muted-foreground">({watchlist?.length})</span>
              </Button>
            </div>
            <div className="flex gap-2 flex-wrap">
              {(watchlist ?? []).slice(0, 8).map((item) => (
                <Link key={item.ticker} to={item.lastEvaluationId ? `/evaluation/${item.lastEvaluationId}` : '/analyze'}>
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border bg-card hover:border-primary/50 transition-colors text-sm">
                    <span className="font-mono font-bold">{item.ticker}</span>
                    {item.lastEvaluationStatus && (
                      <StatusBadge status={item.lastEvaluationStatus} size="sm" />
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Quick tips if no API */}
        {(evaluations?.length ?? 0) === 0 && (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              {
                icon: '🔑',
                title: 'Connect an API',
                desc: 'Add your Alpha Vantage or FMP key in Settings to auto-fetch financial data.',
                action: { label: 'Go to Settings', href: '/settings' },
              },
              {
                icon: '📁',
                title: 'Upload CSV Data',
                desc: "Don't have an API key? Upload annual financial statements as CSV files.",
                action: { label: 'Analyze with CSV', href: '/analyze' },
              },
              {
                icon: '☁️',
                title: 'Sync with Nostr',
                desc: 'Log in with your Nostr key to sync evaluations across devices.',
                action: null,
              },
            ].map((tip) => (
              <Card key={tip.title} className="bg-muted/20">
                <CardContent className="py-5 px-4">
                  <div className="text-3xl mb-3">{tip.icon}</div>
                  <h3 className="font-semibold text-sm mb-1">{tip.title}</h3>
                  <p className="text-xs text-muted-foreground mb-3">{tip.desc}</p>
                  {tip.action && (
                    <Button variant="outline" size="sm" asChild>
                      <Link to={tip.action.href}>{tip.action.label}</Link>
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
